import { createTheme } from '@mui/material/styles';

// Enterprise regulatory-intelligence visual language:
// dense information hierarchy, restrained colour, minimal rounding, borders over shadows.

export const sidebar = {
  bg: '#0B1220',
  bgHover: '#151F32',
  border: '#1E293B',
  text: '#94A3B8',
  textActive: '#FFFFFF',
  textMuted: '#64748B',
  accent: '#3B82F6',
};

export const risk = {
  critical: '#DC2626',
  high: '#EA580C',
  medium: '#D97706',
  low: '#16A34A',
  neutral: '#6B7280',
};

export const workspace = {
  background: '#E7EBF1',
  customisingBackground: '#E3E8EF',
  card: '#FFFFFF',
  cardSecondary: '#F8FAFC',
  border: '#D6DCE6',
  radius: 15,
  controlRadius: 9,
  shadow: '0 2px 10px rgba(15, 23, 42, 0.08)',
  raisedShadow: '0 8px 22px rgba(15, 23, 42, 0.10)',
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2450C6',
      dark: '#1B3A94',
      light: '#5B7FDB',
    },
    secondary: {
      main: '#0F766E',
    },
    background: {
      default: workspace.background,
      paper: '#FFFFFF',
    },
    text: {
      primary: '#0F172A',
      secondary: '#64748B',
    },
    divider: '#E2E6EC',
    error: { main: '#DC2626' },
    warning: { main: '#D97706' },
    success: { main: '#16A34A' },
    info: { main: '#2450C6' },
  },
  shape: {
    borderRadius: 6,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 600, letterSpacing: '-0.01em' },
    h5: { fontWeight: 600, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    overline: {
      fontWeight: 700,
      letterSpacing: '0.08em',
      fontSize: '0.7rem',
    },
    button: { fontWeight: 600, textTransform: 'none' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: workspace.background },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 6 },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          minWidth: 0,
          backgroundImage: 'none',
          border: `1px solid ${workspace.border}`,
          borderRadius: workspace.radius,
          boxShadow: workspace.shadow,
          '@media (max-width:600px)': {
            '& > .MuiStack-root > .MuiBox-root': {
              flexWrap: 'wrap',
              alignItems: 'flex-start',
            },
            '& > .MuiStack-root > .MuiBox-root > .MuiBox-root': {
              minWidth: 0,
              flex: '1 1 calc(100% - 56px)',
            },
          },
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          minWidth: 0,
          border: `1px solid ${workspace.border}`,
          borderRadius: workspace.radius,
          boxShadow: workspace.shadow,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 600,
        },
        label: { paddingLeft: 8, paddingRight: 8, overflowWrap: 'anywhere' },
        sizeSmall: { fontSize: '0.7rem', height: 22 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: '#F9FAFB',
          fontWeight: 700,
          fontSize: '0.7rem',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: '#64748B',
          borderBottom: '1px solid #E2E6EC',
        },
        root: {
          borderBottom: '1px solid #EEF1F5',
          overflowWrap: 'anywhere',
        },
      },
    },
    MuiTypography: {
      styleOverrides: { root: { overflowWrap: 'anywhere' } },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: '#F9FAFB' },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: '1px solid #E2E6EC',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          boxShadow: 'none',
          maxWidth: '100vw',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: { minWidth: 0, overflowWrap: 'anywhere' },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: { '@media (max-width:600px)': { minWidth: 0, flexWrap: 'wrap' } },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: { '@media (max-width:600px)': { width: '100%', minWidth: 0 } },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: { minWidth: 0, '@media (max-width:600px)': { maxWidth: '100%' } },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: { '@media (max-width:600px)': { fontSize: '0.95rem' } },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: { minWidth: 0 },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: { maxWidth: '100%', overflowX: 'auto' },
      },
    },
  },
});

export default theme;
